import React, { useCallback, useEffect } from 'react';
import moment from 'moment';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  TextInput,
  RequiredFieldIndicator,
  Icon,
  SelectDropdown,
  DatePicker,
} from '@gsa/afp-component-library';
import { emSpaceUnicode } from '../../../../utilities/constants';
import { useContractLine } from '../provider/contract-line-provider';
import { ReadOnlyWrapper } from '../helpers/ec-components';

export const formatPrice = (price, style, currency) => {
  return new Intl.NumberFormat('en-US', {
    style,
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(price);
};

const validationSchema = yup.object().shape({
  unitPrice: yup
    .number()
    .transform((value) =>
      value === '' || Number.isNaN(value) ? undefined : value,
    )
    .integer('Unit price must be a whole number.')
    .min(1, 'Unit price must be $1 or more.')
    .max(999999999, 'Unit price must be less than $1,000,000,000'),
  quantity: yup
    .number()
    .transform((value) =>
      value === '' || Number.isNaN(value) ? undefined : value,
    )
    .moreThan(0, 'Allocation quantity must be 1 or more.')
    .max(32767, 'Allocation quantity must be less than 32,768.'), // SMALL INT max value
  shipmentDays: yup
    .number()
    .transform((value) =>
      value === '' || Number.isNaN(value) ? undefined : value,
    )
    .integer('Shipping days must be a whole number.')
    .moreThan(0, 'Shipping days must be between 0 and 999.')
    .lessThan(1000, 'Shipping days must be between 0 and 999.'),
  shipmentOrDelivery: yup.string(),
  deliveryDate: yup.date(),
  assemblyPoint: yup.string(),
  inspectionPoint: yup.string(),
  directFlag: yup.boolean(),
  vin: yup.string().max(17),
  additionalInformation: yup.string().max(500),
  coFirstName: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      'is-valid-co-first-name',
      'CO first name must contain only alphabetic characters.',
      (value) => !value || /^[A-Za-z]+$/.test(value),
    ),
  coLastName: yup
    .string()
    .nullable()
    .notRequired()
    .test(
      'is-valid-co-last-name',
      'CO last name must contain only alphabetic characters.',
      (value) => !value || /^[A-Za-z]+$/.test(value),
    ),
  coEmail: yup
    .string()
    .notRequired()
    .nullable()
    .test('is-valid-email', 'Invalid email address.', (value) => {
      if (!value || /^[^\s@]+@[^\s@]+\.[^\s@]{2,63}$/.test(value)) {
        return yup.string().email().isValidSync(value);
      }
      return true;
    }),
});

const NonSOPInfo = () => {
  const {
    lineTabData,
    setLineTabData,
    detailTabData,
    setDetailTabData,

    contractLine,
    vendorLocations,
    makeAvailableForOrdering,
    setMakeAvailableForOrdering,
    canUpdateContract,
  } = useContractLine();

  useEffect(() => {
    setLineTabData({});
    setDetailTabData({});
  }, []);

  const { orderEndDate, orderStartDate } = contractLine;
  const purchaseTypeCode =
    contractLine?.contractHeader?.solicitation?.purchaseTypeCode;
  const isNonSOP = purchaseTypeCode === 'N' || purchaseTypeCode === 'O';
  const isMAS = purchaseTypeCode === 'M';
  const isExpress = purchaseTypeCode === 'E';
  const isAwardedInFleet = contractLine?.contractHeader?.fleetAwarded;
  const showAssemblyPointInput = isNonSOP;
  const showInspectionPointInput = isNonSOP || isMAS;

  const assemblyPointOptions =
    vendorLocations
      ?.filter((location) => location?.commonCode?.code === 'AsmPoint')
      .map((location) => ({
        label: `${location.associatedLocationName} - ${location.address1}, ${location.city}, ${location.state?.stateName} ${location.postalCode}`,
        value: location.id,
      })) || [];
  const inspectionPointOptions =
    vendorLocations
      ?.filter((location) => location?.commonCode?.code === 'InspPoint')
      .map((location) => ({
        label: `${location.associatedLocationName} - ${location.address1}, ${location.city}, ${location.state?.stateName} ${location.postalCode}`,
        value: location.id,
      })) || [];

  const defaultAssemblyPoint =
    contractLine?.associatedLocations?.[0]?.vendorAssemblyPointId;
  const defaultInspetionPoint =
    contractLine?.associatedLocations?.[0]?.vendorInspectionPointId;

  const {
    control,
    formState: { errors },
    setError,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  useEffect(() => {
    reset({
      unitPrice: contractLine?.contractLineFinancialRef?.unitPrice || '',
      quantity: contractLine?.quantity || '',
      shipmentDays: contractLine?.shipmentDays || '',
      shipmentOrDelivery: contractLine?.shipmentOrDelivery || '',
      deliveryDate: moment.utc(contractLine?.deliveryDate).format('YYYY-MM-DD'),
      assemblyPoint: defaultAssemblyPoint || '',
      inspectionPoint: defaultInspetionPoint || '',
      isDirectFlag: contractLine?.isDirectFlag || '',
      vin: contractLine?.vin || '',
      additionalInformation: contractLine?.additionalInformation || '',
      coFirstName: contractLine?.coFirstName || '',
      coLastName: contractLine?.coLastName || '',
      coEmail: contractLine?.coEmail || '',
    });
  }, [contractLine]);

  const genLabel = (label, required) => (
    <span className="text-bold" data-testid={label}>
      {label} {required && <RequiredFieldIndicator />}
    </span>
  );

  const handleBlur = useCallback(
    (fieldName, value) => {
      setLineTabData((prev) => {
        const newValue = value;
        const currentValue =
          contractLine?.[fieldName] || lineTabData?.[fieldName];
        if (newValue === currentValue) {
          return prev;
        }
        return {
          ...prev,
          [fieldName]: newValue,
        };
      });
    },
    [contractLine, lineTabData],
  );

  useEffect(() => {
    if (orderEndDate && orderStartDate) {
      const startDate = new Date(orderStartDate);
      const endDate = new Date(orderEndDate);
      const currentDate = new Date();

      setMakeAvailableForOrdering(
        startDate <= currentDate && currentDate <= endDate ? 'Yes' : 'No',
      );
    }
  }, [orderEndDate, orderStartDate]);

  const makeAvailabeleForOrderingField = (
    <div className="grid-col flex-1">
      <Controller
        name="makeAvailableForOrdering"
        control={control}
        render={({ field: { onBlur } }) => (
          <ReadOnlyWrapper>
            <SelectDropdown
              name="makeAvailableForOrdering"
              value={makeAvailableForOrdering}
              data-testid="makeAvailableForOrdering"
              label={genLabel('Make available for ordering', false)}
              type="number"
              min={0}
              errorMessage={errors.makeAvailableForOrdering?.message}
              onBlur={onBlur}
              onChange={(e) => {
                setMakeAvailableForOrdering(e.target.value);
              }}
              options={[
                { label: '- Select -', value: '' },
                { label: 'Yes', value: 'Yes' },
                { label: 'No', value: 'No' },
              ]}
              disabled={!canUpdateContract}
            />
          </ReadOnlyWrapper>
        )}
      />
    </div>
  );

  const isDirectFlag =
    lineTabData?.isDirectFlag || contractLine?.isDirectFlag || '';

  return (
    <div className="grid-container padding-0 margin-top-2">
      <div className="grid-row grid-gap-4 margin-bottom-4">
        {isExpress && (
          <div className="grid-col flex-1">
            <Controller
              name="vin"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <ReadOnlyWrapper>
                  <TextInput
                    name="vin"
                    value={value === 0 ? emSpaceUnicode : value}
                    data-testid="vin"
                    type="string"
                    label={genLabel('VIN', false)}
                    errorMessage={errors.vin?.message}
                    onChange={onChange}
                    onBlur={(e) => {
                      if (!errors.vin) {
                        onBlur(e);
                        handleBlur('vin', e.target?.value);
                      }
                    }}
                    disabled={!canUpdateContract}
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
        )}
        <div className="grid-col flex-1">
          <Controller
            name="unitPrice"
            control={control}
            required
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper dataType="currency">
                <TextInput
                  name="unitPrice"
                  value={value === 0 ? emSpaceUnicode : value}
                  data-testid="current_price"
                  type="number"
                  min={0}
                  required
                  label={genLabel('Unit price', false)}
                  prefix={<Icon iconName="attach_money" />}
                  errorMessage={errors.unitPrice?.message}
                  onChange={onChange}
                  onBlur={(e) => {
                    if (!errors.unitPrice) {
                      onBlur(e);
                      setLineTabData((prev) => {
                        const newValue = value ? Number.parseFloat(value) : '';
                        const currentValue =
                          contractLine?.contractLineFinancialRef?.unitPrice ||
                          lineTabData?.contractLineFinancialRef?.unitPrice;
                        if (newValue === currentValue) {
                          return prev;
                        }
                        return {
                          ...prev,
                          contractLineFinancialRef: {
                            ...prev.contractLineFinancialRef,
                            unitPrice: newValue,
                          },
                        };
                      });
                    }
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
        <div className="grid-col flex-1">
          <Controller
            name="quantity"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper>
                <TextInput
                  name="quantity"
                  value={value}
                  data-testid="quantity"
                  label={genLabel('Quantity', false)}
                  type="number"
                  required
                  min={1}
                  max={32767}
                  errorMessage={errors.quantity?.message}
                  onChange={onChange}
                  onBlur={(e) => {
                    if (!errors.quantity) {
                      onBlur(e);
                      handleBlur('quantity', e.target?.value);
                    }
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>

        {!isExpress && makeAvailabeleForOrderingField}
        <div className="grid-col flex-1" />
        <div className="grid-col flex-1" />
      </div>

      <div className="grid-row grid-gap-4 margin-bottom-4">
        <div className="grid-col">
          <Controller
            name="shipmentDays"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper>
                <TextInput
                  name="shipmentDays"
                  value={value}
                  data-testid="shipmentDays"
                  label={genLabel('Shipment days', false)}
                  type="number"
                  min={0}
                  required
                  errorMessage={errors.shipmentDays?.message}
                  onChange={onChange}
                  onBlur={(e) => {
                    if (!errors.shipmentDays) {
                      onBlur(e);
                      setDetailTabData((prev) => {
                        const newValue = value;
                        const currentValue =
                          contractLine?.shipmentDays ||
                          detailTabData?.shipmentDays;
                        if (newValue === currentValue) {
                          return prev;
                        }
                        return {
                          ...prev,
                          shipmentDays: newValue,
                        };
                      });
                    }
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
        <div className="grid-col">
          <Controller
            name="shipmentOrDelivery"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper dataType="select">
                <SelectDropdown
                  name="shipmentOrDelivery"
                  value={value}
                  data-testid="shipmentOrDelivery"
                  label={genLabel('Delivery/Shipment', false)}
                  type="number"
                  min={0}
                  required
                  errorMessage={errors.shipmentOrDelivery?.message}
                  onBlur={(e) => {
                    if (!errors.shipmentOrDelivery) {
                      onBlur(e);
                      handleBlur('shipmentOrDelivery', e.target?.value);
                    }
                  }}
                  onChange={onChange}
                  options={[
                    { label: '- Select -', value: '' },
                    { label: 'Shipment', value: 'S' },
                    { label: 'Delivery', value: 'D' },
                  ]}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
        <div className="grid-col">
          <Controller
            name="deliveryDate"
            control={control}
            render={({ field: { value, onBlur } }) => {
              return (
                <ReadOnlyWrapper dataType="date">
                  <DatePicker
                    key={value || 'empty-date'}
                    id="deliveryDate"
                    name="deliveryDate"
                    defaultValue={
                      value ? moment(value).format('YYYY-MM-DD') : undefined
                    }
                    labelClass="text-bold"
                    label={genLabel('Delivery date', isAwardedInFleet)}
                    onChange={(newValue) => {
                      const newDate = moment(newValue, 'MM/DD/YYYY');
                      if (!newDate.isValid()) {
                        return;
                      }
                      const newDateStr = newDate.format('YYYY-MM-DD');
                      if (newDateStr === value) {
                        return;
                      }
                      setValue('deliveryDate', newDateStr);
                      setLineTabData({
                        ...lineTabData,
                        deliveryDate: newDateStr,
                      });
                    }}
                    onBlur={(e) => {
                      if (!e.target?.value) {
                        handleBlur('deliveryDate', '');
                        setLineTabData({
                          ...lineTabData,
                          deliveryDate: '',
                        });
                        return;
                      }

                      if (!errors.deliveryDate) {
                        onBlur(e);
                      }
                    }}
                    disabled={!canUpdateContract}
                  />
                </ReadOnlyWrapper>
              );
            }}
          />
        </div>
        <div className="grid-col flex-1" />
        <div className="grid-col flex-1" />
      </div>

      <div className="grid-row grid-gap-4 margin-bottom-4">
        {showAssemblyPointInput && (
          <div className="grid-col">
            <Controller
              name="assemblyPoint"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <ReadOnlyWrapper dataType="select">
                  <SelectDropdown
                    name="assemblyPoint"
                    value={value}
                    data-testid="assemblyPoint"
                    label={genLabel('Assembly point', false)}
                    type="number"
                    min={0}
                    errorMessage={errors.assemblyPoint?.message}
                    onBlur={(e) => {
                      if (!errors.assemblyPoint) {
                        onBlur(e);
                        setDetailTabData((prevData) => ({
                          ...prevData,
                          associatedLocations: [
                            {
                              ...(contractLine?.associatedLocations?.[0] || []),
                              ...(prevData?.associatedLocations?.[0] || []),
                              vendorAssemblyPointId: e?.target?.value
                                ? Number.parseInt(e?.target?.value, 10)
                                : null,
                            },
                          ],
                        }));
                      }
                    }}
                    onChange={onChange}
                    options={[
                      { label: '- Select -', value: '' },
                      ...assemblyPointOptions,
                    ]}
                    disabled={!canUpdateContract}
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
        )}
        {showInspectionPointInput && (
          <div className="grid-col flex-1">
            <Controller
              name="inspectionPoint"
              control={control}
              render={({ field: { value, onChange, onBlur } }) => (
                <ReadOnlyWrapper dataType="select">
                  <SelectDropdown
                    name="inspectionPoint"
                    value={value}
                    data-testid="inspectionPoint"
                    label={genLabel('Inspection point', false)}
                    type="number"
                    min={0}
                    errorMessage={errors.inspectionPoint?.message}
                    onBlur={(e) => {
                      if (!errors.inspectionPoint) {
                        onBlur(e);
                        setDetailTabData((prevData) => ({
                          ...prevData,
                          associatedLocations: [
                            {
                              ...(contractLine?.associatedLocations?.[0] || []),
                              ...(prevData?.associatedLocations?.[0] || []),
                              vendorInspectionPointId: Number.parseInt(
                                e?.target?.value,
                                10,
                              ),
                            },
                          ],
                        }));
                      }
                    }}
                    onChange={onChange}
                    options={[
                      { label: '- Select -', value: '' },
                      ...inspectionPointOptions,
                    ]}
                    disabled={!canUpdateContract}
                  />
                </ReadOnlyWrapper>
              )}
            />
          </div>
        )}

        <div className="grid-col flex-1 margin-top-3">
          {genLabel('Direct flag', false)}
          <div className="padding-top-1">{isDirectFlag || 'No'}</div>
        </div>

        {isExpress && makeAvailabeleForOrderingField}
        {!isExpress && <div className="grid-col flex-1" />}
        <div className="grid-col flex-1" />
        {!showAssemblyPointInput && <div className="grid-col flex-1" />}
        {!showInspectionPointInput && <div className="grid-col flex-1" />}
      </div>

      <div className="grid-row grid-gap-4 margin-bottom-4">
        <div className="grid-col">
          <Controller
            name="additionalInformation"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper>
                <TextInput
                  name="additionalInformation"
                  data-testid="additionalInformation"
                  type="textarea"
                  label={genLabel('Additional information', false)}
                  characterLimit={500}
                  value={value}
                  onChange={onChange}
                  onBlur={(e) => {
                    if (!errors.additionalInformation) {
                      onBlur(e);
                      handleBlur('additionalInformation', e.target?.value);
                    }
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
      </div>

      <div
        className="title-s-caps text-primary margin-top-6"
        style={{ borderBottom: '1px solid #DFE1E2' }}
      >
        CONTRACTING OFFICER INFO
      </div>

      <div className="grid-row grid-gap-4 margin-bottom-2">
        <div className="grid-col flex-1">
          <Controller
            name="coFirstName"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper>
                <TextInput
                  name="coFirstName"
                  value={value === 0 ? emSpaceUnicode : value}
                  data-testid="coFirstName"
                  type="string"
                  maxLength={255}
                  label={genLabel('First name', isAwardedInFleet)}
                  errorMessage={errors.coFirstName?.message}
                  onChange={onChange}
                  onBlur={(e) => {
                    onBlur(e);
                    handleBlur('coFirstName', e.target?.value);
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
        <div className="grid-col flex-1">
          <Controller
            name="coLastName"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper>
                <TextInput
                  name="coLastName"
                  value={value === 0 ? emSpaceUnicode : value}
                  data-testid="coLastName"
                  label={genLabel('Last name', isAwardedInFleet)}
                  type="string"
                  maxLength={255}
                  errorMessage={errors.coLastName?.message}
                  onChange={onChange}
                  onBlur={(e) => {
                    onBlur(e);
                    handleBlur('coLastName', e.target?.value);
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
        <div className="grid-col flex-1" />
        <div className="grid-col flex-1" />
        <div className="grid-col flex-1" />
      </div>

      <div className="grid-row grid-gap-4">
        <div className="grid-col flex-1">
          <Controller
            name="coEmail"
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <ReadOnlyWrapper>
                <TextInput
                  name="coEmail"
                  value={value === 0 ? emSpaceUnicode : value}
                  data-testid="coEmail"
                  label={genLabel('Email', isAwardedInFleet)}
                  type="string"
                  maxLength={255}
                  errorMessage={errors.coEmail?.message}
                  onChange={onChange}
                  onBlur={(e) => {
                    onBlur(e);
                    if (
                      !value ||
                      /^[^\s@]+@[^\s@]+\.[^\s@]{2,63}$/.test(value)
                    ) {
                      handleBlur('coEmail', e.target?.value);
                    } else {
                      setError('coEmail', {
                        message: 'Invalid email address.',
                      });
                    }
                  }}
                  disabled={!canUpdateContract}
                />
              </ReadOnlyWrapper>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default NonSOPInfo;
